import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import SimpleBar from 'simplebar-react';
import ReactDOMServer from 'react-dom/server';
import { isMobile } from 'react-device-detect';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import Draggable from '../../components/DraggableCompat';
import { closeModal, updateTimestamp } from '../../actions/modal';
import { Modal } from '..';
import Form from '../Form';
import Strings from '../../Strings';
import './Wizard.scss';
import './FloatingModal.scss';
import ModalsVisibilityMenu from '../../modals/_ModalsMetadata/ModalsVisibilityMenu';
import WizardNavigation from '../../modals/_ModalsMetadata/WizardNavigation';

export const wizardTypes = {
  wizard: 'wizard',
  floatingModal: 'floatingModal',
  horizontal: 'horizontal',
};

export const getTooltipFromValidationErrors = validationErrors => {
  return validationErrors && validationErrors?.length > 0
    ? validationErrors?.map(v =>
      v.errors?.map(e => `${v.propertyName}: ${e}`)).flat()?.
      concat(validationErrors?.filter(v => v.missing)?.map(v => `${v.propertyName} is missing`)).
      map(s => <div>{s}</div>)
    : undefined;
};

const Wizard = React.forwardRef(({ type = wizardTypes.wizard, showPagesFilter = true, openMinimized = false, ...props }, scrollbarRef) => {
  const { pages, bringOnTop, isTopModal, scrollToPage, scrollToPageSignal, forceNextPage, forceMinimizedOrMaximized } = props;
  const [nextButtonHovered, setNextButtonHovered] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [collapsed, setCollapsed] = useState(openMinimized);
  const lastHandledScrollSignalRef = useRef(0);

  const scrollableNodeRef = useRef();
  const pagesRef = useRef(type === wizardTypes.horizontal ? pages.reduce((acc, r) => ({ ...acc, [r.id]: React.createRef() }), {}) : {});
  useImperativeHandle(scrollbarRef, () => scrollableNodeRef.current, []);

  const onWizardPageClicked = pageId => {
    const pageRef = pagesRef.current?.[pageId];
    const pageNode = pageRef?.current;
    if (!pageNode || !scrollableNodeRef.current) {
      return;
    }
    scrollableNodeRef.current.scrollTo({ top: pageNode.offsetTop - 10, behavior: 'smooth' });
  };

  useEffect(() => {
    if (type !== wizardTypes.horizontal) {
      return;
    }
    const nextRefs = {};
    pages.forEach(p => {
      nextRefs[p.id] = pagesRef.current?.[p.id] || React.createRef();
    });
    pagesRef.current = nextRefs;
  }, [pages, type]);

  useEffect(() => {
    if (type === wizardTypes.floatingModal && forceMinimizedOrMaximized) {
      setCollapsed(forceMinimizedOrMaximized === 'minimized');
    }
  }, [forceMinimizedOrMaximized, type]);

  useEffect(() => {
    if (type !== wizardTypes.horizontal || pages.length === 0 || !scrollToPage || !scrollToPageSignal) {
      return;
    }

    if (lastHandledScrollSignalRef.current === scrollToPageSignal) {
      return;
    }

    const pageExists = pages.some(p => p.id === scrollToPage);
    if (!pageExists) {
      return;
    }

    lastHandledScrollSignalRef.current = scrollToPageSignal;
    onWizardPageClicked(scrollToPage);
  }, [type, pages, scrollToPage, scrollToPageSignal]);

  useEffect(() => {
    if (currentPage > pages.length - 1) {
      setCurrentPage(p => p - 1);
    }
  }, [pages]);

  const previousPageNo = number => {
    return number === 0 ? null : number - 1;
  };

  const nextPageNo = number => {
    return number === pages.length - 1 ? null : number + 1;
  };

  const onNext = func => {
    if (nextPageNo(currentPage) === null && props.onSubmit) {
      props.onSubmit();
      return;
    }

    let canGoNext = true;
    const nextPage = nextPageNo(currentPage);
    if (func) {
      canGoNext = func();
    }
    if (canGoNext === false) {
      return;
    }
    if (canGoNext === true && nextPage) {
      setCurrentPage(nextPage);
    }
    if (canGoNext !== true && canGoNext !== false) {
      Promise.resolve(canGoNext).then(value => {
        if (value && nextPage) {
          setCurrentPage(nextPage);
        }
      });
    }
    if (canGoNext && nextPage) setCurrentPage(nextPage);
  };

  useEffect(() => {
    if (forceNextPage > 0) {
      onNext();
    }
  }, [forceNextPage]);

  const onPrevious = () => {
    const prevPage = previousPageNo(currentPage);
    if (prevPage !== null) setCurrentPage(prevPage);
  };

  const getNextButtonText = () => {
    return nextPageNo(currentPage) === null ? Strings.save : Strings.next;
  };

  const showBackButton = () => {
    if (previousPageNo(currentPage) !== null) {
      return (
        <div>
          <button className="brand-white-gray back-button" onClick={onPrevious} data-testid="back-button">
            {Strings.back}
          </button>
        </div>
      );
    }
    return <div>{'\xa0'}</div>;
  };

  const onNextButtonHover = value => {
    if (props.onNextButtonHover) {
      props.onNextButtonHover(value);
    }
    setNextButtonHovered(value);
  };

  const onCancel = () => {
    if (props.onBeforeCancel && props.onBeforeCancel() === false) {
      return;
    }
    if (props.onCancelAction) {
      props.onCancelAction();
    }
    props.onCancel();
  };

  const floatingModalRightButtons = (
    <>
      <div className="buttonContainer">
        <button className="close-button" key="close" type="button" onClick={props.onCancel} data-testid="close-button" />
      </div>
      <div className="buttonContainer">
        <button
          className={`minimize-button ${collapsed ? 'collapsed' : ''}`}
          key="minimize"
          type="button"
          onClick={() => setCollapsed(!collapsed)}
        />
      </div>
    </>
  );

  const floatingModal = (
    <Draggable cancel=".modalContent, .modalTabs" onStart={bringOnTop}>
      {!collapsed ? (
        <div
          className={`modalWrapper floating-modal ${isTopModal ? 'top-modal' : ''} ${isMobile ? 'mobile' : ''} ${props.name
            } ${props.className || ''}`}
          onClick={bringOnTop}
        >
          <header className="modalHeader" data-testid="modal-header">
            {pages[currentPage]?.title}
            {pages.length > 1 ? (
              <div className="steps">
                {Array.from({ length: pages.length }, (_, i) => i).map(i => (
                  <div
                    key={`step-${i}`}
                    className={`step ${i < currentPage || pages[i].canGoNext ? 'completed' : ''}`}
                  />
                ))}
              </div>
            ) : (
              <></>
            )}
          </header>
          {pages.length > 1 ? (
            <div className="modalTabs">
              {Array.from({ length: pages.length }, (_, i) => i).map(i => (
                <div
                  className={`
                  tab ${i === currentPage ? 'active' : ''}
                  ${i < currentPage ? 'clickable' : ''}
                `}
                  onClick={() => {
                    setCurrentPage(i);
                  }}
                  key={`tab-${i}`}
                >
                  {pages[i].title}
                </div>
              ))}
            </div>
          ) : (
            <></>
          )}
          {pages[currentPage] && (
            <div className="modalContent">
              {pages[currentPage].content}
              <div className="button-bar" key="button-bar">
                {props.customButtons ? (
                  props.customButtons
                ) : (
                  <div
                    className="bottom-button-container"
                    onMouseEnter={() => onNextButtonHover(true)}
                    onMouseLeave={() => onNextButtonHover(false)}
                    data-tooltip-content={pages[currentPage].nextButton?.tooltip || props.nextButtonTooltip}
                    data-tooltip-id={pages[currentPage].nextButton?.tooltip || props.nextButtonTooltip ? 'tooltip' : undefined}
                  >
                    <button
                      className="brand-blue"
                      key="submit"
                      type="button"
                      onClick={() => onNext(pages[currentPage].onNext)}
                      disabled={!pages[currentPage].canGoNext}
                      data-testid="submit"
                    >
                      {pages[currentPage].nextButton?.text || getNextButtonText()}
                    </button>
                  </div>
                )}
                <div className="right">{floatingModalRightButtons}</div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          className={`modalWrapper floating-modal minimized ${isMobile ? 'mobile' : ''} ${isTopModal ? 'top-modal' : ''
            } ${props.name} ${props.className || ''}`}
          onClick={bringOnTop}
        >
          <div className="modalContentMinimized">
            <div className="button-bar" key="button-bar">
              {pages[currentPage].titleMinimized ? pages[currentPage].titleMinimized : pages[currentPage].title}
              <div className="right">{floatingModalRightButtons}</div>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );

  const wizard = (
    <Modal name={props.name} withoutContainer {...props}>
      {pages[currentPage] && (
        <div className={`wizard ${props.name} ${type} ${props.className || ''}`}>
          <header className="wizardHeader" data-testid="wizard-header">
            <div className="horizontal-flex center header-content">
              {showBackButton()}
              {pages[currentPage].title}
              <div>
                {showPagesFilter && props.pagesVisibility?.length > 1 && <ModalsVisibilityMenu modalId={props.name} />}
                {props.editModeButton && (
                  <div className={`edit-button-container ${props.editModeButton.readOnly ? '' : 'edit-mode-on'}`}>
                    <button
                      className={`edit-button ${props.editModeButton.readOnly ? '' : 'edit-mode-on'}`}
                      onClick={props.editModeButton.onClick}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="requiredFieldsError">
              {nextButtonHovered && pages[currentPage].emptyFieldsCount > 0
                ? `${pages[currentPage].emptyFieldsCount} *${Strings.fieldsRequired}`
                : '\xa0'}
            </div>
          </header>
          <div className="wizardContent">
            <Form onCancel={onCancel} id={props.name} key={pages[currentPage].id}>
              {pages[currentPage].noScrollbar ? (
                <div className="content-block">{pages[currentPage].content}</div>
              ) : (
                <OverlayScrollbarsComponent
                  defer
                  className="scroll-content"
                  key="form"
                  options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
                  events={{ scroll: props.onScroll ? props.onScroll : undefined }}
                  ref={scrollableNodeRef}
                >
                  <div className="content-block">{pages[currentPage].content}</div>
                </OverlayScrollbarsComponent>
              )}
              {props.customButtons ? (
                props.customButtons
              ) : (
                <div className="buttons" key="button-bar">
                  <div className="bottom-button-container">
                    <button className="brand-white-gray" key="cancel" type="cancel" disabled={pages[currentPage]?.closeButton?.disabled}>
                      {pages[currentPage]?.closeButton?.text || Strings.close}
                    </button>
                  </div>
                  <div
                    className="bottom-button-container"
                    onMouseEnter={() => onNextButtonHover(true)}
                    onMouseLeave={() => onNextButtonHover(false)}
                    data-tooltip-html={ReactDOMServer.renderToStaticMarkup(pages[currentPage.nextButton?.tooltip] || props.nextButtonTooltip)}
                    data-tooltip-id={pages[currentPage].nextButton?.tooltip || props.nextButtonTooltip ? 'tooltip' : undefined}
                  >
                    <button
                      className="brand-blue"
                      key="submit"
                      type="button"
                      onClick={() => onNext(pages[currentPage].onNext)}
                      disabled={!(pages[currentPage].canGoNext || props.onSubmitEnabled === true)}
                    >
                      {pages[currentPage].nextButton?.text || getNextButtonText()}
                    </button>
                  </div>
                </div>
              )}
            </Form>
          </div>
        </div>
      )}
    </Modal>
  );

  const horizontalWizard = (
    <Modal name={props.name} withoutContainer {...props}>
      {pages?.length > 0 && (
        <div className={`wizard ${props.name} ${type} ${props.className || ''}`}>
          <header className="wizardHeader" data-testid="wizard-header">
            <div className="horizontal-flex center header-content unequal-children">
              {props.title}
              {!props.hideStagesProgress && pages.map(p => p.stage).filter((value, index, array) => array.indexOf(value) === index).length > 1 && (
                <div className="horizontal-flex unequal-children">
                  {pages.map(p => p.stage).filter((value, index, array) => array.indexOf(value) === index).map(stage => {
                    const missing = pages.filter(p => p.stage === stage).map(p => p.emptyFieldsCount).reduce((a, b) => a + b, 0);
                    const total = pages.filter(p => p.stage === stage).map(p => p.requiredFieldsCount).reduce((a, b) => a + b, 0);
                    const progress = total ? ((total - missing) / total) * 100 : 100;
                    const completed = pages.find(p => p.stage === stage)?.stage.stageCompleted;
                    const stageName = pages.find(p => p.stage === stage)?.stage.stageName;
                    return (
                      <div key={stage.name}>
                        <div className={`stage-progress ${completed ? 'completed' : ''}`}>{stageName}</div>
                        <div className="progress-bar" style={{ '--progress': `${progress}%` }} />
                      </div>
                    );
                  })}
                </div>
              )}
              {props.pages?.length > 0 && showPagesFilter && <WizardNavigation pages={props.pages} onClick={onWizardPageClicked} currentPage={undefined} />}
            </div>
            <div className="requiredFieldsError" />
          </header>
          <div className="wizardContent">
            <Form onCancel={onCancel} id={props.name} key={props.name}>
              <SimpleBar
                className="scroll-content"
                key="form"
                scrollableNodeProps={{ ref: scrollableNodeRef }}
                onScroll={props.onScroll ? props.onScroll : () => { }}
              >
                <div className="content-block">{pages.map((p, i) => (
                  <React.Fragment key={p.id}>
                    {!props.hideStages && p.stage?.stageName && (i === 0 || p.stage?.stageName !== pages[i - 1].stage?.stageName) && (
                      <div className="grid-full-width">
                        <div className="stage-title">{p.stage.stageName}</div>
                      </div>
                    )}
                    <div className="grid-full-width">
                      <div className="page-info-chip" ref={pagesRef.current[p.id]}>
                        <div className={`missing-fields-count ${p.wrongFieldsCount ? 'not-valid' : 'valid-color'}`}>{p.wrongFieldsCount > 0 ? p.wrongFieldsCount : ''}</div>
                        {p.name}
                      </div>
                    </div>
                    {p.content}
                  </React.Fragment>
                ))}
                </div>
              </SimpleBar>
              {props.customButtons ? (
                props.customButtons
              ) : (
                <div className="buttons" key="button-bar">
                  <div className="bottom-button-container">
                    <button className="brand-white-gray" key="cancel" type="cancel">
                      {pages[currentPage]?.closeButton?.text || Strings.close}
                    </button>
                  </div>
                  <div
                    className="bottom-button-container"
                    onMouseEnter={() => onNextButtonHover(true)}
                    onMouseLeave={() => onNextButtonHover(false)}
                    data-tooltip-html={ReactDOMServer.renderToStaticMarkup(pages[currentPage]?.nextButton?.tooltip || props.nextButtonTooltip)}
                    data-tooltip-id={pages[currentPage]?.nextButton?.tooltip || props.nextButtonTooltip ? 'tooltip' : undefined}
                  >
                    <button
                      className="brand-blue"
                      key="submit"
                      type="button"
                      onClick={props.onSubmit}
                      disabled={!(pages[currentPage]?.canGoNext || props.onSubmitEnabled === true)}
                    >
                      {Strings.save}
                    </button>
                  </div>
                </div>
              )}
            </Form>
          </div>
        </div>
      )}
    </Modal>
  );

  return type === wizardTypes.wizard ? wizard : type === wizardTypes.horizontal ? horizontalWizard : floatingModal;
});

Wizard.propTypes = {
  name: PropTypes.string.isRequired,
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string.isRequired,
      titleMinimized: PropTypes.string,
      content: PropTypes.any.isRequired,
      emptyFieldsCount: PropTypes.number,
      wrongFieldsCount: PropTypes.number,
      onNext: PropTypes.func,
      canGoNext: PropTypes.bool,
      closeButton: PropTypes.shape({
        text: PropTypes.string,
      }),
      nextButton: PropTypes.shape({
        text: PropTypes.string,
        tooltip: PropTypes.string,
      }),
    }),
  ).isRequired,
  onNextButtonHover: PropTypes.func,
  onCancel: PropTypes.func,
  onBeforeCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  forceNextPage: PropTypes.number,
  scrollToPage: PropTypes.string,
  scrollToPageSignal: PropTypes.number,
  customButtons: PropTypes.any,
  type: PropTypes.string,
  bringOnTop: PropTypes.func,
  isTopModal: PropTypes.bool,
  showPagesFilter: PropTypes.bool,
  pagesVisibility: PropTypes.array,
  onScroll: PropTypes.bool,
  openMinimized: PropTypes.bool,
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onCancel: () => dispatch(closeModal(ownProps.name, {}, ownProps.id)),
  bringOnTop: () => dispatch(updateTimestamp(ownProps.name, ownProps.id)),
});

const mapStateToProps = (state, ownProps) => {
  return {
    pagesVisibility: state.modalsVisibility.pagesVisibility.find(m => m.id === ownProps.name)?.pages,
  };
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(Wizard);
